import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, ArrowRight, Check, X, Briefcase, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

// Dynamically determine API base URL:
// - On production: use relative '/api' (ingress routes this to backend)
// - On preview/development with REACT_APP_BACKEND_URL: use that URL
const getApiBase = () => {
  // Check if we have an explicit backend URL configured
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  
  // If environment variable is set and not empty, use it
  if (envUrl && envUrl.trim() !== '') {
    return `${envUrl}/api`;
  }
  
  // Default to relative path (works on production where ingress routes /api to backend)
  return '/api';
};

const API = getApiBase();

// Helper function to get full image URL
const getImageUrl = (url) => {
  if (!url) return '';
  
  // Cloudinary URLs are already absolute and work everywhere
  if (url.includes('cloudinary.com')) return url;
  
  // External URLs (http/https) - return as-is
  if (url.startsWith('http')) return url;
  
  // Relative URLs starting with /api/uploads - return as-is (will use current origin)
  if (url.startsWith('/')) return url;
  
  return url;
};

const HomePage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [showJobModal, setShowJobModal] = useState(false);
  const [jobFormData, setJobFormData] = useState({
    name: '',
    email: '',
    phone: '',
    function: '',
    experience: '0-2',
    message: ''
  });
  const [cvFile, setCvFile] = useState(null);
  const [submittingJob, setSubmittingJob] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    service: 'algemeen',
    message: ''
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const categories = [
    { id: 'all', label: 'Alles' },
    { id: 'nieuwbouw', label: 'Nieuwbouw & Renovatie' },
    { id: 'hellende-platte-daken', label: 'Hellende & Platte Daken' },
    { id: 'gevel-goot', label: 'Gevel & Gootbekleding' },
    { id: 'zink-koper', label: 'Zink & Koperwerken' },
    { id: 'isolatie', label: 'Isolatiewerken' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(p => p.category === selectedCategory));
    }
  }, [selectedCategory, projects]);

  const fetchData = async () => {
    try {
      const [servicesRes, projectsRes, contentRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/content`)
      ]);

      setServices(servicesRes.data);
      setProjects(projectsRes.data);
      setFilteredProjects(projectsRes.data);
      setContent(contentRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Kon de gegevens niet laden');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('service', formData.service);
      formDataToSend.append('message', formData.message);
      
      // Add files
      files.forEach((file) => {
        formDataToSend.append('files', file);
      });

      await axios.post(`${API}/contact`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Bericht succesvol verzonden! We nemen zo snel mogelijk contact met u op.');
      setFormData({ name: '', email: '', phone: '', subject: '', service: 'algemeen', message: '' });
      setFiles([]);
      // Reset file input
      const fileInput = document.getElementById('file-input');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Error submitting contact form:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Er is een fout opgetreden. Probeer het opnieuw.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleJobInputChange = (e) => {
    setJobFormData({ ...jobFormData, [e.target.name]: e.target.value });
  };

  const handleCvChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCvFile(file);
    }
  };

  const handleJobSubmit = async (e) => {
    e.preventDefault();
    setSubmittingJob(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', jobFormData.name);
      formDataToSend.append('email', jobFormData.email);
      formDataToSend.append('phone', jobFormData.phone);
      formDataToSend.append('function', jobFormData.function);
      formDataToSend.append('experience', jobFormData.experience);
      formDataToSend.append('message', jobFormData.message);
      
      if (cvFile) {
        formDataToSend.append('cv', cvFile);
      }

      await axios.post(`${API}/jobs/apply`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Sollicitatie succesvol verzonden! We nemen contact met u op.');
      setJobFormData({ name: '', email: '', phone: '', function: '', experience: '0-2', message: '' });
      setCvFile(null);
      setShowJobModal(false);
    } catch (error) {
      console.error('Error submitting job application:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Er is een fout opgetreden. Probeer het opnieuw.');
      }
    } finally {
      setSubmittingJob(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Navbar */}
      <nav className="navbar" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img src="/logo.png" alt="Vanden Heede Logo" className="h-10 sm:h-12 w-auto" />
            </div>
            <div className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-black hover:text-red-600 transition-colors font-medium" data-testid="nav-home">Home</button>
              <button onClick={() => scrollToSection('diensten')} className="text-black hover:text-red-600 transition-colors font-medium" data-testid="nav-diensten">Diensten</button>
              <button onClick={() => scrollToSection('referenties')} className="text-black hover:text-red-600 transition-colors font-medium" data-testid="nav-referenties">Referenties</button>
              <button onClick={() => scrollToSection('contact')} className="text-black hover:text-red-600 transition-colors font-medium" data-testid="nav-contact">Contact</button>
            </div>
            <button 
              onClick={() => scrollToSection('contact')} 
              className="btn-primary text-xs sm:text-sm px-3 sm:px-6 py-2 sm:py-3"
              data-testid="nav-offerte-btn"
            >
              <span className="hidden sm:inline">Vrijblijvende </span>Offerte
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero-section" data-testid="hero-section">
        {/* Video Background */}
        <div className="absolute inset-0 w-full h-full overflow-hidden z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full w-auto h-auto object-cover"
            style={{ objectFit: 'cover' }}
          >
            <source src="/hero-video.mp4" type="video/mp4" />
          </video>
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        </div>
        
        <div className="hero-content max-w-7xl mx-auto px-6 h-[75vh] flex items-center">
          <div className="max-w-4xl">
            <div className="text-red-500 text-sm font-semibold tracking-wider uppercase mb-4" data-testid="hero-welcome">
              Welkom op de website van
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight" data-testid="hero-title">
              DAKWERKEN<br />
              VANDEN HEEDE BV
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-12 leading-relaxed max-w-2xl whitespace-pre-line" data-testid="hero-subtitle">
              {content?.hero_subtitle || 'Uw dakwerker in Wortegem-Petegem en regio Oudenaarde. Specialist in dakrenovatie, asbestverwijdering en nieuwbouw.\n10 jaar garantie op alle werken!'}
            </p>

            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => scrollToSection('diensten')} 
                className="btn-primary"
                data-testid="hero-diensten-btn"
              >
                Onze Diensten <ArrowRight className="inline ml-2" size={20} />
              </button>
              <button 
                onClick={() => scrollToSection('contact')} 
                className="btn-outline" 
                style={{ color: '#ffffff', borderColor: '#ffffff' }}
                data-testid="hero-contact-btn"
              >
                Neem Contact Op
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="diensten" className="py-16 bg-white" data-testid="diensten-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-red-600 text-sm font-semibold tracking-wider uppercase mb-3">Wat we aanbieden</div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'Poppins' }}>Onze Diensten</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-base sm:text-lg">
              Dakwerken in Oudenaarde, Waregem, Anzegem en omstreken? Van dakrenovatie tot asbestverwijdering - vraag vrijblijvend een offerte aan!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="services-grid">
            {services.map((service, index) => (
              <div key={service.id} className="service-card rounded-2xl p-8" data-testid={`service-card-${index}`}>
                <div className="service-icon mb-6">
                  <i className={service.icon}></i>
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Poppins' }}>{service.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">{service.description}</p>
                <button 
                  onClick={() => scrollToSection('contact')} 
                  className="text-red-600 font-semibold hover:underline text-sm"
                  data-testid={`service-offerte-btn-${index}`}
                >
                  Vrijblijvende offerte? <ArrowRight className="inline ml-1" size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects/References Section */}
      <section id="referenties" className="py-16 bg-gray-50" data-testid="referenties-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-red-600 text-sm font-semibold tracking-wider uppercase mb-3">Ons Werk</div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'Poppins' }}>Referenties</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-base sm:text-lg">
              Bekijk onze gerealiseerde projecten in de regio Oudenaarde en Waregem
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12 category-filter" data-testid="category-filter">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={selectedCategory === cat.id ? 'active' : ''}
                data-testid={`category-btn-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          {filteredProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="projects-grid">
              {filteredProjects.map((project, index) => (
                <div 
                  key={project.id} 
                  className="project-card cursor-pointer" 
                  onClick={() => setSelectedProject(project)}
                  data-testid={`project-card-${index}`}
                >
                  <div className="overflow-hidden">
                    <img src={getImageUrl(project.image_url)} alt={project.title} className="project-image" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Poppins' }}>{project.title}</h3>
                    <p className="text-gray-600 text-sm">{project.description}</p>
                    {project.images && project.images.length > 0 && (
                      <p className="text-red-600 text-xs mt-3 font-semibold">
                        +{project.images.length + 1} foto&apos;s - Klik voor galerij
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12" data-testid="no-projects">
              <p className="text-gray-500 text-base">Geen projecten beschikbaar voor deze categorie.</p>
            </div>
          )}
        </div>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-[9999] flex items-center justify-center p-4"
          onClick={() => { setSelectedProject(null); setCurrentMediaIndex(0); }}
          data-testid="project-modal"
        >
          <div 
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex-shrink-0 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">{selectedProject.title}</h2>
              <button
                onClick={() => { setSelectedProject(null); setCurrentMediaIndex(0); }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                data-testid="close-modal-btn"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Media Carousel */}
            <div className="flex-shrink-0 relative bg-gray-100">
              {(() => {
                // Combine all media: main image + additional images + videos
                const allMedia = [
                  { type: 'image', url: getImageUrl(selectedProject.image_url) },
                  ...(selectedProject.images || []).map(url => ({ type: 'image', url: getImageUrl(url) })),
                  ...(selectedProject.videos || []).map(url => ({ type: 'video', url: getImageUrl(url) }))
                ];
                const currentMedia = allMedia[currentMediaIndex];
                
                // Helper function to get YouTube embed URL
                const getYouTubeEmbedUrl = (url) => {
                  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
                  const match = url.match(regExp);
                  return match && match[2].length === 11 
                    ? `https://www.youtube.com/embed/${match[2]}` 
                    : url;
                };
                
                // Check if URL is YouTube
                const isYouTube = (url) => url.includes('youtube.com') || url.includes('youtu.be');
                
                return (
                  <>
                    <div className="aspect-video">
                      {currentMedia.type === 'image' ? (
                        <img 
                          src={currentMedia.url} 
                          alt={selectedProject.title}
                          className="w-full h-full object-contain"
                        />
                      ) : isYouTube(currentMedia.url) ? (
                        <iframe
                          src={getYouTubeEmbedUrl(currentMedia.url)}
                          className="w-full h-full"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={`${selectedProject.title} video`}
                        />
                      ) : (
                        <video 
                          src={currentMedia.url}
                          className="w-full h-full object-contain"
                          controls
                        />
                      )}
                    </div>
                    
                    {/* Navigation Arrows */}
                    {allMedia.length > 1 && (
                      <>
                        <button
                          onClick={() => setCurrentMediaIndex(prev => prev === 0 ? allMedia.length - 1 : prev - 1)}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition-all"
                        >
                          <ChevronLeft size={24} />
                        </button>
                        <button
                          onClick={() => setCurrentMediaIndex(prev => prev === allMedia.length - 1 ? 0 : prev + 1)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition-all"
                        >
                          <ChevronRight size={24} />
                        </button>
                        
                        {/* Dots Indicator */}
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                          {allMedia.map((media, idx) => (
                            <button
                              key={idx}
                              onClick={() => setCurrentMediaIndex(idx)}
                              className={`w-2 h-2 rounded-full transition-all ${
                                idx === currentMediaIndex 
                                  ? 'bg-white w-4' 
                                  : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
            
            {/* Thumbnail Strip */}
            {(() => {
              const allMedia = [
                { type: 'image', url: getImageUrl(selectedProject.image_url) },
                ...(selectedProject.images || []).map(url => ({ type: 'image', url: getImageUrl(url) })),
                ...(selectedProject.videos || []).map(url => ({ type: 'video', url: getImageUrl(url) }))
              ];
              
              if (allMedia.length <= 1) return null;
              
              return (
                <div className="flex-shrink-0 px-4 py-3 bg-gray-100 border-b">
                  <div className="flex gap-2 overflow-x-auto">
                    {allMedia.map((media, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentMediaIndex(idx)}
                        className={`relative flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 transition-all ${
                          idx === currentMediaIndex ? 'border-red-600' : 'border-transparent opacity-60 hover:opacity-100'
                        }`}
                      >
                        {media.type === 'image' ? (
                          <img src={media.url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                            <Play size={16} className="text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <p className="text-gray-700 mb-4">{selectedProject.description}</p>
              <p className="text-sm text-gray-500">
                <strong>Categorie:</strong> {categories.find(c => c.id === selectedProject.category)?.label || selectedProject.category}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Social Media Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-red-600 text-sm font-semibold tracking-wider uppercase mb-3">Volg Ons</div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">Social Media</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-base sm:text-lg">
              Blijf op de hoogte van onze laatste projecten op Facebook
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Facebook</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Volg ons op Facebook voor updates en projecten
                </p>
                <a 
                  href="https://www.facebook.com/DakwerkenVandenHeede/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-semibold transition-all transform hover:scale-105"
                  data-testid="facebook-cta"
                >
                  Bezoek Facebook
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Instagram</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Ontdek onze realisaties op Instagram
                </p>
                <a 
                  href="https://www.instagram.com/dakwerkenvandenheedebv" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-3 rounded-full font-semibold transition-all transform hover:scale-105"
                  data-testid="instagram-cta"
                >
                  Bezoek Instagram
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-red-600 text-sm font-semibold tracking-wider uppercase mb-3">Neem Contact Op</div>
            <h2 className="text-4xl sm:text-5xl font-bold" style={{ fontFamily: 'Poppins' }}>Contactformulier</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="contact-form" data-testid="contact-form">
              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-2">Uw naam *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-2">Uw E-mailadres *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    data-testid="contact-email-input"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-2">Uw mobiele telefoonnummer</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-2">Dienst *</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    data-testid="contact-service-select"
                  >
                    <option value="algemeen">Algemeen</option>
                    <option value="nieuwbouw-renovatie">Nieuwbouw & Renovatie</option>
                    <option value="hellende-platte-daken">Hellende & Platte Daken</option>
                    <option value="gevel-goot">Gevel & Gootbekleding</option>
                    <option value="zink-koper">Zink & Koperwerken</option>
                    <option value="isolatie">Isolatiewerken</option>
                  </select>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-2">Onderwerp van het bericht *</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    data-testid="contact-subject-input"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-2">Bericht *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    data-testid="contact-message-input"
                  ></textarea>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-2">
                    Plannen uploaden (optioneel)
                  </label>
                  <p className="text-xs text-gray-600 mb-2">
                    Toegestane bestandsformaten: PDF, DWG, DWT, DXF, RVT, IFC, SKP, JPG, PNG (max 10MB per bestand)
                  </p>
                  <input
                    id="file-input"
                    type="file"
                    multiple
                    accept=".pdf,.dwg,.dwt,.dxf,.rvt,.ifc,.skp,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="form-input"
                    data-testid="contact-file-input"
                  />
                  {files.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-semibold mb-1">Geselecteerde bestanden:</p>
                      <ul className="text-sm text-gray-600">
                        {files.map((file, index) => (
                          <li key={index}>• {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <button 
                  type="submit" 
                  className="btn-primary w-full" 
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                >
                  {submitting ? 'Verzenden...' : 'Verzend Bericht'}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div data-testid="contact-info">
              <div className="bg-black text-white rounded-2xl p-8 mb-6">
                <h3 className="text-2xl font-bold mb-8" style={{ fontFamily: 'Poppins' }}>Contactgegevens</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <MapPin className="text-red-600 flex-shrink-0" size={24} />
                    <div>
                      <h4 className="font-semibold mb-1">Adres</h4>
                      <a 
                        href="https://www.google.com/maps/search/?api=1&query=Kortrijkstraat+174+9790+Wortegem-Petegem"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-300 hover:text-red-600 transition-colors text-sm"
                      >
                        {content?.address || 'Kortrijkstraat 174, 9790 Wortegem-Petegem'}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <Phone className="text-red-600 flex-shrink-0" size={24} />
                    <div>
                      <h4 className="font-semibold mb-1">Telefoon</h4>
                      <a href={`tel:${content?.phone || '0494344106'}`} className="text-gray-300 hover:text-red-600 transition-colors text-sm">
                        {content?.phone || '0494 34 41 06'}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <Mail className="text-red-600 flex-shrink-0" size={24} />
                    <div>
                      <h4 className="font-semibold mb-1">Email</h4>
                      <a href={`mailto:${content?.email || 'info@dakwerkenvandenheede.be'}`} className="text-gray-300 hover:text-red-600 transition-colors text-sm">
                        {content?.email || 'info@dakwerkenvandenheede.be'}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Google Maps */}
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                <iframe
                  src="https://maps.google.com/maps?q=Kortrijkstraat%20174%2C%209790%20Wortegem-Petegem%2C%20Belgium&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Locatie Dakwerken Vanden Heede"
                  className="w-full"
                ></iframe>
              </div>

              {/* Waarom kiezen voor ons - Uitgebreide versie */}
              <div className="mt-6 bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                <h4 className="font-bold mb-4 text-black text-lg">Waarom kiezen voor Dakwerken Vanden Heede?</h4>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <Check className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-sm text-gray-800"><strong>10 jaar garantie</strong> op alle dakwerken</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Check className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-sm text-gray-800"><strong>Erkend asbestverwijderaar</strong> met jaarlijks attest en bijscholing</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Check className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-sm text-gray-800"><strong>Jarenlange ervaring</strong> in nieuwbouw en renovatie</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Check className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-sm text-gray-800"><strong>Persoonlijke service</strong> en opvolging van A tot Z</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Check className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-sm text-gray-800"><strong>Gratis offerte</strong> binnen 48 uur</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Check className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-sm text-gray-800"><strong>Actief in de regio:</strong> Wortegem-Petegem, Oudenaarde, Waregem, Anzegem, Avelgem, Kluisbergen, Maarkedal en omstreken</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* FAQ Sectie */}
          <div className="mt-16" data-testid="faq-section">
            <h3 className="text-2xl font-bold mb-8 text-center" style={{ fontFamily: 'Poppins' }}>Veelgestelde Vragen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-2">Wat kost een dakrenovatie?</h4>
                <p className="text-gray-600 text-sm">De prijs van een dakrenovatie hangt af van de grootte, het materiaal en de staat van uw dak. Gemiddeld kost een dakrenovatie tussen €80 en €150 per m². Vraag een gratis offerte aan voor een exacte prijs.</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-2">Hoe lang duurt een dakrenovatie?</h4>
                <p className="text-gray-600 text-sm">Een gemiddelde dakrenovatie duurt 3 tot 7 werkdagen, afhankelijk van de omvang en weersomstandigheden. Bij asbestverwijdering kan dit iets langer duren door de veiligheidsmaatregelen.</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-2">Zijn jullie erkend voor asbestverwijdering?</h4>
                <p className="text-gray-600 text-sm">Ja, wij zijn een erkend asbestverwijderaar met het officiële attest. Onze medewerkers volgen jaarlijks verplichte bijscholing om veilig en conform de wetgeving te werken.</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-2">Welke garantie geven jullie?</h4>
                <p className="text-gray-600 text-sm">Wij geven <strong>10 jaar garantie</strong> op al onze dakwerken. Dit geldt voor zowel materiaal als plaatsing, zodat u jarenlang zorgeloos kunt genieten van uw dak.</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-2">In welke regio zijn jullie actief?</h4>
                <p className="text-gray-600 text-sm">Wij werken in een straal van 25 km rond Wortegem-Petegem: Oudenaarde, Waregem, Anzegem, Avelgem, Kluisbergen, Maarkedal, Kruishoutem, Zwalm, Deinze en omstreken.</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-2">Zijn er premies voor dakisolatie?</h4>
                <p className="text-gray-600 text-sm">Ja, in Vlaanderen kunt u premies krijgen voor dakisolatie via uw netbeheerder (Fluvius). De premie kan oplopen tot €8 per m². Wij helpen u graag met de aanvraag.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-10" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="bg-white rounded-2xl p-6 inline-block mb-4">
                <img src="/logo.png" alt="Vanden Heede Logo" className="h-12 w-auto mb-3" />
                <p className="text-gray-800 text-sm max-w-xs">
                  {content?.about_text || 'Voor zowel nieuwbouw als renovatie bent u verzekerd van kwaliteit.'}
                </p>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Snelle Links</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-red-600 transition-colors">Home</button></li>
                <li><button onClick={() => scrollToSection('diensten')} className="text-gray-400 hover:text-red-600 transition-colors">Diensten</button></li>
                <li><button onClick={() => scrollToSection('referenties')} className="text-gray-400 hover:text-red-600 transition-colors">Referenties</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-red-600 transition-colors">Contact</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Volg Ons</h4>
              <div className="space-y-3">
                <a 
                  href="https://www.facebook.com/DakwerkenVandenHeede/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-gray-400 hover:text-red-600 transition-colors"
                  data-testid="facebook-link"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <span className="text-sm">Facebook</span>
                </a>
                <a 
                  href="https://www.instagram.com/dakwerkenvandenheedebv" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-gray-400 hover:text-red-600 transition-colors"
                  data-testid="instagram-link"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  <span className="text-sm">Instagram</span>
                </a>
              </div>
              <h4 className="font-bold mb-4 mt-6">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>{content?.address || 'Kortrijkstraat 174, 9790 Wortegem-Petegem'}</li>
                <li>{content?.phone || '0494 34 41 06'}</li>
                <li>{content?.email || 'info@dakwerkenvandenheede.be'}</li>
              </ul>
              <h4 className="font-bold mb-3 mt-6">Werkgebied</h4>
              <p className="text-sm text-gray-400">
                Wortegem-Petegem, Oudenaarde, Waregem, Anzegem, Avelgem, Kluisbergen, Maarkedal, Kruishoutem, Zwalm, Deinze en omstreken (straal 25km)
              </p>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} {content?.company_name || 'Dakwerken Vanden Heede BV'}. Alle rechten voorbehouden.</p>
            <p className="mt-2">Dakwerker in Oudenaarde, Waregem, Anzegem en regio | 10 jaar garantie | Erkend asbestverwijderaar</p>
            <p className="mt-3 text-gray-400" data-testid="footer-credit">
              Met veel liefde <span className="text-red-600">♥</span> gemaakt door{' '}
              <a
                href="https://www.websitebestellen.be"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-red-600 transition-colors underline-offset-2 hover:underline"
                data-testid="footer-credit-link"
              >
                www.websitebestellen.be
              </a>
            </p>
            <button
              onClick={() => navigate('/admin/login')}
              className="mt-4 text-xs text-gray-500 hover:text-red-600 transition-colors"
              data-testid="admin-login-link"
            >
              Admin Login
            </button>
          </div>
        </div>
      </footer>

      {/* Floating Action Button - Sollicitaties - Top Right */}
      <button
        onClick={() => setShowJobModal(true)}
        className="fixed top-[72px] sm:top-24 right-4 sm:right-8 bg-red-600 hover:bg-red-700 text-white rounded-full px-3 sm:px-6 py-2 sm:py-3 shadow-2xl transition-all transform hover:scale-105 z-40 flex items-center space-x-2 sm:space-x-3 shake-attention"
        data-testid="job-fab"
        aria-label="Solliciteer nu"
      >
        <Briefcase size={18} className="sm:w-6 sm:h-6" />
        <span className="font-semibold text-xs sm:text-sm whitespace-nowrap">Wij zoeken jou!</span>
      </button>

      {/* Job Application Modal */}
      {showJobModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-[9999] flex items-center justify-center p-4"
          onClick={() => setShowJobModal(false)}
          data-testid="job-modal"
        >
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4 flex items-center justify-between z-10 rounded-t-2xl">
              <div className="flex items-center space-x-3">
                <Briefcase size={28} />
                <h2 className="text-2xl font-bold">Join us!</h2>
              </div>
              <button
                onClick={() => setShowJobModal(false)}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                data-testid="close-job-modal-btn"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 mb-4 text-sm">
                We zijn altijd op zoek naar <strong>gemotiveerde</strong> vakmensen! Vul onderstaand formulier in en we nemen zo snel mogelijk contact met je op.
              </p>
              
              <form onSubmit={handleJobSubmit} data-testid="job-application-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold mb-1">Naam *</label>
                    <input
                      type="text"
                      name="name"
                      value={jobFormData.name}
                      onChange={handleJobInputChange}
                      className="form-input text-sm py-2"
                      required
                      data-testid="job-name-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-semibold mb-1">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={jobFormData.email}
                      onChange={handleJobInputChange}
                      className="form-input text-sm py-2"
                      required
                      data-testid="job-email-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-semibold mb-1">Telefoon *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={jobFormData.phone}
                      onChange={handleJobInputChange}
                      className="form-input text-sm py-2"
                      required
                      data-testid="job-phone-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-semibold mb-1">Gewenste functie *</label>
                    <input
                      type="text"
                      name="function"
                      value={jobFormData.function}
                      onChange={handleJobInputChange}
                      className="form-input text-sm py-2"
                      placeholder="Bijv. Dakdekker, Helper"
                      required
                      data-testid="job-function-input"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold mb-1">Ervaring *</label>
                    <select
                      name="experience"
                      value={jobFormData.experience}
                      onChange={handleJobInputChange}
                      className="form-input text-sm py-2"
                      required
                      data-testid="job-experience-select"
                    >
                      <option value="0-2">0-2 jaar</option>
                      <option value="2-5">2-5 jaar</option>
                      <option value="5-10">5-10 jaar</option>
                      <option value="10+">10+ jaar</option>
                    </select>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold mb-1">Motivatie *</label>
                    <textarea
                      name="message"
                      value={jobFormData.message}
                      onChange={handleJobInputChange}
                      className="form-input text-sm py-2"
                      rows={3}
                      placeholder="Vertel ons waarom je bij ons wilt werken..."
                      required
                      data-testid="job-message-input"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold mb-1">CV uploaden (optioneel)</label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleCvChange}
                      className="form-input text-sm py-2"
                      data-testid="job-cv-input"
                    />
                    {cvFile && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ {cvFile.name} ({(cvFile.size / 1024 / 1024).toFixed(2)} MB)
                      </p>
                    )}
                  </div>
                </div>
                
                <button 
                  type="submit" 
                  className="btn-primary w-full mt-4" 
                  disabled={submittingJob}
                  data-testid="job-submit-btn"
                >
                  {submittingJob ? 'Verzenden...' : 'Sollicitatie Versturen'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;